import "./UpgradeOrTopupDialog.scss";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Coins, ArrowRight, Sparkles, CheckIcon } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { IRootReducerState } from "@/store/reducers";
import { useEffect, useState, useCallback } from "react";
import { getSubscriptions, getTopupPlans, getUsersTokensUtilization } from "@/store/actions/meta.action";
import { useNavigate } from "react-router-dom";
import { PAYMENTS_ROUTE } from "@/constants/RoutesConfig";
import TopupPlansDialog from "@/components/common-components/topup-plans-dialog/TopupPlansDialog";
import FeatureRestrictionWrapper from "@/components/common-components/feature-restriction/FeatureRestrictionWrapper";
import { CommonService } from "@/services";
import { Misc } from "@/constants";
import { IAPIResponseType } from "@/models/api.model";
import { userLogin } from "@/store/actions/auth.action";
import ENV from "@/constants/ENV";

interface CostProvider {
    plan_id: string;
    is_active: boolean;
}

interface CostStructure {
    currency: string;
    amount: number;
    providers: {
        razorpay: CostProvider;
    } | null;
}

interface PlanDetails {
    description: string;
    features: string[];
    cost_structure: {
        IN: CostStructure;
        US?: CostStructure;
        GLOBAL: CostStructure;
    };
}

interface SubscriptionPlan {
    id: string;
    plan_name: string;
    plan_name_tag: string;
    plan_interval: string;
    plan_details: PlanDetails;
    is_public_plan: boolean;
    allowed_spend_budget_USD: number;
    is_topup_plan: boolean;
    is_domestic: boolean;
    created_at: string;
    updated_at: string;
}

interface UpgradeOrTopupDialogProps {
    isOpen: boolean;
    onClose: () => void;
    message?: string;
}

const UpgradeOrTopupDialog = ({
    isOpen,
    onClose,
    message = "You need more credits to continue using AI features."
}: UpgradeOrTopupDialogProps) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [isTopupDialogOpen, setIsTopupDialogOpen] = useState(false);
    const [isGeneratingSubscription, setIsGeneratingSubscription] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'ai-agents' | 'ai-human'>('ai-agents');

    // Get user and subscription data from Redux
    const currentUser = useSelector((state: IRootReducerState) => state.auth?.user);
    const subscriptions = useSelector((state: IRootReducerState) => state.meta.subscriptions);
    const subscriptionsLoading = useSelector((state: IRootReducerState) => state.meta.subscriptionsLoading);
    const usersBalance = useSelector((state: IRootReducerState) => state.meta.usersBalance);
    const token = CommonService._localStorage.getItem(Misc.LOCAL_STORAGE_JWT_TOKEN);

    // Fetch subscriptions and balance on mount
    useEffect(() => {
        if (isOpen) {
            dispatch(getSubscriptions());
            dispatch(getUsersTokensUtilization());
            dispatch(getTopupPlans());
        }
    }, [isOpen, dispatch]);

    // Determine if user is on free plan
    const userSubscription = currentUser?.subscription;
    const currentPlanId = userSubscription?.plan_id;
    const currentPlanDetails = Array.isArray(subscriptions)
        ? (subscriptions as any[]).find((sub: any) => sub.id === currentPlanId)
        : null;
    const currentPlanName = currentPlanDetails?.plan_name || userSubscription?.plan_name || "Free";
    const isFreeUser = !currentPlanId || currentPlanName.toLowerCase() === "free" || !userSubscription;

    // Get available credits
    const availableCredits = usersBalance?.user_remaining_budget || 0;

    // Helper function to get price for current user's region
    const getPriceForRegion = (plan: SubscriptionPlan) => {
        if (!plan?.plan_details?.cost_structure) {
            return { currency: 'USD', amount: 0, providers: null };
        }

        const costStructure = plan.plan_details.cost_structure;

        if (plan.is_domestic && costStructure.IN) {
            return costStructure.IN;
        } else if (costStructure.US) {
            return costStructure.US;
        } else {
            return costStructure.GLOBAL || { currency: 'USD', amount: 0, providers: null };
        }
    };

    // Fetch user details after payment
    const fetchUserDetails = useCallback(() => {
        const token = CommonService._localStorage.getItem(Misc.LOCAL_STORAGE_JWT_TOKEN);
        if (token) {
            CommonService._account.CheckLoginAPICall(token)
                .then((response: IAPIResponseType<any>) => {
                    setIsLoading(false);
                    dispatch(userLogin(response?.data, token, ""));
                    onClose();
                })
                .catch(() => {
                    dispatch(userLogin(null, "", ""));
                    setIsLoading(false);
                });
        } else {
            setIsLoading(false);
        }
    }, [dispatch, onClose]);

    // Check transaction after payment
    const checkTransaction = useCallback((subscriptionId: string) => {
        CommonService._payments_Service.checkTransaction(subscriptionId)
            .then((response: IAPIResponseType<any>) => {
                CommonService.handleMixpanelTrack('payment_success', {
                    transaction_id: subscriptionId,
                    plan_id: selectedPlan?.id,
                    plan_name: selectedPlan?.plan_name,
                    amount: selectedPlan ? getPriceForRegion(selectedPlan).amount : 0,
                    currency: selectedPlan ? getPriceForRegion(selectedPlan).currency : 'USD',
                    timestamp: new Date().toISOString()
                });

                CommonService._alert.showToast(response?.data?.message || response?.message || "Transaction completed successfully!");
                fetchUserDetails();
                setTimeout(() => {
                    dispatch(getUsersTokensUtilization());
                }, 500);
                setIsLoading(false);
            })
            .catch((error: IAPIResponseType<any>) => {
                CommonService.handleMixpanelTrack('payment_failed', {
                    transaction_id: subscriptionId,
                    plan_id: selectedPlan?.id,
                    plan_name: selectedPlan?.plan_name,
                    error_message: error?.message || "Transaction failed",
                    timestamp: new Date().toISOString()
                });

                setIsLoading(false);
                CommonService._alert.showToast(error?.message || "An error occurred", "error");
            });
    }, [fetchUserDetails, selectedPlan, dispatch]);

    // Handle Razorpay subscription
    const handleSubscribe = useCallback(async (transactionId: string, subscription_id: string) => {
        try {
            CommonService.handleMixpanelTrack('payment_initiated', {
                transaction_id: transactionId,
                plan_id: selectedPlan?.id,
                plan_name: selectedPlan?.plan_name,
                amount: selectedPlan ? getPriceForRegion(selectedPlan).amount : 0,
                currency: selectedPlan ? getPriceForRegion(selectedPlan).currency : 'USD',
                payment_provider: 'razorpay',
                timestamp: new Date().toISOString()
            });

            const options = {
                key: ENV.RAZORPAY_KEY || 'rzp_live_PQjOObQvLhhMOS',
                subscription_id: subscription_id,
                name: 'SuperEngineer',
                description: 'Subscription Payment',
                handler: function () {
                    setIsLoading(true);
                    setTimeout(() => {
                        checkTransaction(transactionId);
                    }, 5000);
                },
                prefill: {
                    name: currentUser?.name,
                    email: currentUser?.email,
                    contact: ''
                },
                theme: {
                    color: '#3399cc'
                },
                modal: {
                    ondismiss: function () {
                        CommonService.handleMixpanelTrack('payment_cancelled', {
                            transaction_id: transactionId,
                            plan_id: selectedPlan?.id,
                            plan_name: selectedPlan?.plan_name,
                            timestamp: new Date().toISOString()
                        });
                    }
                }
            };
            const rzp = new (window as any).Razorpay(options);
            rzp.open();
        } catch (error: any) {
            CommonService.handleMixpanelTrack('payment_initiation_failed', {
                transaction_id: transactionId,
                plan_id: selectedPlan?.id,
                plan_name: selectedPlan?.plan_name,
                error_message: error?.message || "Failed to initiate payment",
                timestamp: new Date().toISOString()
            });
            console.error('Error creating subscription:', error);
        }
    }, [checkTransaction, currentUser, selectedPlan]);

    // Generate subscription
    const generateSubscription = useCallback((plan: SubscriptionPlan) => {
        if (!token) {
            navigate('/login?returnUrl=' + encodeURIComponent(location.pathname + location.search));
            onClose();
            return;
        }
        const payload = {
            plan_id: plan?.id
        };
        setSelectedPlan(plan);
        setIsGeneratingSubscription(true);
        CommonService._payments_Service.createTransaction(payload)
            .then((response: any) => {
                handleSubscribe(response?.data?.transaction_id, response?.data?.rz_transaction_id);
                setSelectedPlan(null);
                setIsGeneratingSubscription(false);
            })
            .catch((error: any) => {
                CommonService.handleMixpanelTrack('subscription_generation_failed', {
                    plan_id: plan?.id,
                    plan_name: plan?.plan_name,
                    error_message: error?.message || "Failed to generate subscription",
                    timestamp: new Date().toISOString()
                });

                setSelectedPlan(null);
                setIsGeneratingSubscription(false);
            });
    }, [handleSubscribe, token, navigate, onClose]);

    // Handle upgrade to paid plan (view all plans)
    const handleUpgrade = () => {
        navigate(PAYMENTS_ROUTE);
        onClose();
    };

    // Handle buy credits
    const handleBuyCredits = () => {
        setIsTopupDialogOpen(true);
        onClose();
    };

    // Plan hierarchy for checking current/lower tier
    const PLAN_HIERARCHY: { [key: string]: number } = {
        'test_id': 0,      // Free tier
        'pro_id': 1,       // Pro tier
        'advisory_id': 2,  // Advisory/Enterprise tier
        'startup_id': 3    // Startup tier (highest)
    };

    // Filter plans based on active tab (like payments page)
    const getFilteredPlans = () => {
        if (!subscriptions || !Array.isArray(subscriptions)) {
            return [];
        }

        return (subscriptions as SubscriptionPlan[]).filter((plan: SubscriptionPlan) => {
            const planName = plan.plan_name?.toLowerCase() || '';

            if (activeTab === 'ai-agents') {
                // AI Agents tab: Free and Pro plans
                return planName === 'free' || planName === 'pro';
            } else {
                // AI Agent + Human Support tab: Advisory, Startup plans
                return planName === 'advisory' || planName === 'startup';
            }
        });
    };

    const filteredPlans = getFilteredPlans();

    const isPlanDisabled = (planId: string) => {
        const currentLevel = currentPlanId ? (PLAN_HIERARCHY[currentPlanId] ?? -1) : -1;
        const planLevel = PLAN_HIERARCHY[planId];
        return planLevel === undefined || planLevel <= currentLevel;
    };

    const getButtonText = (plan: SubscriptionPlan) => {
        // For Startup plan, show "Schedule a call"
        if (plan.plan_name?.toLowerCase() === 'startup') {
            return 'Schedule a call';
        }
        if (plan.id === currentPlanId) {
            return 'Current Plan';
        }
        if (isPlanDisabled(plan.id)) {
            return 'Lower Tier';
        }
        return 'Subscribe';
    };

    const handlePlanAction = (plan: SubscriptionPlan) => {
        // For Startup plan, open Calendly
        if (plan.plan_name?.toLowerCase() === 'startup') {
            window.open('https://calendly.com/abdulrahmanjanoo/30min', '_blank');
            CommonService.handleMixpanelTrack('pricing_schedule_call_clicked', {
                plan_name: plan.plan_name,
                plan_id: plan.id,
                source: 'upgrade_dialog'
            });
            return;
        }
        generateSubscription(plan);
    };

    return (
        <>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="upgrade-topup-dialog">
                    <DialogHeader>
                        <DialogTitle className="upgrade-topup-dialog__title">
                            <Sparkles className="upgrade-topup-dialog__title-icon" />
                            {isFreeUser && !message.includes("credits") ? "Upgrade Your Plan" : "Low Credits"}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="upgrade-topup-dialog__content">
                        {/* Message */}
                        <p className="upgrade-topup-dialog__message">
                            {message}
                        </p>

                        {/* Credits display */}
                        {!isFreeUser && (
                            <div className="upgrade-topup-dialog__credits">
                                <Coins className="upgrade-topup-dialog__credits-icon" />
                                <div>
                                    <p className="upgrade-topup-dialog__credits-label">Available Credits</p>
                                    <p className="upgrade-topup-dialog__credits-value">
                                        {availableCredits.toLocaleString()} tokens
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Free user - Show upgrade options */}
                        {isFreeUser ? (
                            <div className="upgrade-topup-dialog__upgrade-section">
                                {/* Tab Navigation */}
                                <div className="upgrade-topup-dialog__tabs">
                                    <button
                                        className={`upgrade-topup-dialog__tab ${activeTab === 'ai-agents' ? 'upgrade-topup-dialog__tab--active' : ''}`}
                                        onClick={() => {
                                            setActiveTab('ai-agents');
                                            CommonService.handleMixpanelTrack('upgrade_dialog_tab_switched', { tab: 'ai-agents' });
                                        }}
                                    >
                                        AI Agents
                                    </button>
                                    <button
                                        className={`upgrade-topup-dialog__tab ${activeTab === 'ai-human' ? 'upgrade-topup-dialog__tab--active' : ''}`}
                                        onClick={() => {
                                            setActiveTab('ai-human');
                                            CommonService.handleMixpanelTrack('upgrade_dialog_tab_switched', { tab: 'ai-human' });
                                        }}
                                    >
                                        AI Agents + Human Support
                                    </button>
                                </div>

                                {subscriptionsLoading ? (
                                    <div className="upgrade-topup-dialog__plans">
                                        {Array.from({ length: activeTab === 'ai-agents' ? 2 : 3 }).map((_, index) => (
                                            <div key={index} className="upgrade-topup-dialog__plan-card-skeleton">
                                                <div className="skeleton-plan-name"></div>
                                                <div className="skeleton-plan-tag"></div>
                                                <div className="skeleton-plan-description"></div>
                                                <div className="skeleton-plan-description-short"></div>
                                                <div className="skeleton-price"></div>
                                                <div className="skeleton-button"></div>
                                                <div className="skeleton-features">
                                                    {Array.from({ length: 4 }).map((_, featureIndex) => (
                                                        <div key={featureIndex} className="skeleton-feature">
                                                            <div className="skeleton-check"></div>
                                                            <div className="skeleton-feature-text"></div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : filteredPlans.length > 0 || activeTab === 'ai-human' ? (
                                    <div className="upgrade-topup-dialog__plans">
                                        {filteredPlans.map((plan: SubscriptionPlan) => {
                                            const priceInfo = getPriceForRegion(plan);
                                            const features = plan.plan_details?.features || [];
                                            const description = plan.plan_details?.description || '';
                                            const planTagName = plan.plan_name_tag || '';
                                            const isDisabled = isPlanDisabled(plan.id);
                                            const buttonText = getButtonText(plan);
                                            const isStartup = plan.plan_name?.toLowerCase() === 'startup';

                                            return (
                                                <div key={plan.id} className={`upgrade-topup-dialog__plan-card ${isDisabled && !isStartup ? 'upgrade-topup-dialog__plan-card--disabled' : ''}`}>
                                                    {isStartup && priceInfo?.currency === 'INR' && (
                                                        <div className="upgrade-topup-dialog__popular-tag">Popular</div>
                                                    )}
                                                    <div className="upgrade-topup-dialog__plan-details">
                                                        <h4 className="upgrade-topup-dialog__plan-name">
                                                            {plan.plan_name ? plan.plan_name.charAt(0).toUpperCase() + plan.plan_name.slice(1) : 'Plan'}
                                                        </h4>
                                                        <div className="upgrade-topup-dialog__plan-tag">
                                                            {planTagName}
                                                        </div>
                                                        <p className="upgrade-topup-dialog__plan-description">
                                                            {description}
                                                        </p>
                                                    </div>

                                                    <div className="upgrade-topup-dialog__plan-price">
                                                        {isStartup ? (
                                                            <div className="upgrade-topup-dialog__discount-price">
                                                                <span className="upgrade-topup-dialog__original-price">
                                                                    {priceInfo?.currency === 'INR' ? '₹2,00,000' : '$4,000'}
                                                                </span>
                                                                <div className="upgrade-topup-dialog__discounted-price">
                                                                    <span className="upgrade-topup-dialog__plan-currency">
                                                                        {priceInfo?.currency === 'INR' ? '₹' : '$'}
                                                                    </span>
                                                                    <span className="upgrade-topup-dialog__plan-amount">
                                                                        {priceInfo?.amount?.toLocaleString() || 0}
                                                                    </span>
                                                                    <span className="upgrade-topup-dialog__plan-period">
                                                                        /{plan.plan_interval || 'month'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <span className="upgrade-topup-dialog__plan-currency">
                                                                    {priceInfo?.currency === 'INR' ? '₹' : '$'}
                                                                </span>
                                                                <span className="upgrade-topup-dialog__plan-amount">
                                                                    {priceInfo?.amount?.toLocaleString() || 0}
                                                                </span>
                                                                <span className="upgrade-topup-dialog__plan-period">
                                                                    /{plan.plan_interval || 'month'}
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>

                                                    <div className="upgrade-topup-dialog__plan-action">
                                                        <Button
                                                            onClick={() => handlePlanAction(plan)}
                                                            className="upgrade-topup-dialog__subscribe-button"
                                                            fullWidth={true}
                                                            disabled={isDisabled && !isStartup}
                                                            loading={isGeneratingSubscription && selectedPlan?.id === plan.id || isLoading}
                                                        >
                                                            {buttonText}
                                                        </Button>
                                                    </div>

                                                    <ul className="upgrade-topup-dialog__plan-features">
                                                        {features.map((feature: string, idx: number) => (
                                                            <li key={idx} className="upgrade-topup-dialog__plan-feature">
                                                                <CheckIcon className="upgrade-topup-dialog__plan-feature-icon" />
                                                                <span>{feature}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            );
                                        })}

                                        {/* Enterprise plan - only show in AI Agent + Human Support tab */}
                                        {activeTab === 'ai-human' && (
                                            <div className="upgrade-topup-dialog__plan-card">
                                                <div className="upgrade-topup-dialog__plan-details">
                                                    <h4 className="upgrade-topup-dialog__plan-name">Enterprise</h4>
                                                    <div className="upgrade-topup-dialog__plan-tag">Fully Customized</div>
                                                    <p className="upgrade-topup-dialog__plan-description">
                                                        For large organizations with unique requirements, advanced compliance needs, and large-scale deployments.
                                                    </p>
                                                </div>

                                                <div className="upgrade-topup-dialog__plan-price">
                                                    <span className="upgrade-topup-dialog__custom-pricing">Custom Pricing</span>
                                                </div>

                                                <div className="upgrade-topup-dialog__plan-action">
                                                    <Button
                                                        onClick={() => {
                                                            window.open('https://calendly.com/abdulrahmanjanoo/30min', '_blank');
                                                            CommonService.handleMixpanelTrack('pricing_enterprise_schedule_call_clicked', {
                                                                plan_name: 'Enterprise',
                                                                source: 'upgrade_dialog'
                                                            });
                                                        }}
                                                        className="upgrade-topup-dialog__subscribe-button"
                                                        fullWidth={true}
                                                    >
                                                        Schedule a call
                                                    </Button>
                                                </div>

                                                <ul className="upgrade-topup-dialog__plan-features">
                                                    <li className="upgrade-topup-dialog__plan-feature">
                                                        <CheckIcon className="upgrade-topup-dialog__plan-feature-icon" />
                                                        <span>Unlimited projects</span>
                                                    </li>
                                                    <li className="upgrade-topup-dialog__plan-feature">
                                                        <CheckIcon className="upgrade-topup-dialog__plan-feature-icon" />
                                                        <span>Deploy on your own cloud or ours</span>
                                                    </li>
                                                    <li className="upgrade-topup-dialog__plan-feature">
                                                        <CheckIcon className="upgrade-topup-dialog__plan-feature-icon" />
                                                        <span>Custom app integrations</span>
                                                    </li>
                                                    <li className="upgrade-topup-dialog__plan-feature">
                                                        <CheckIcon className="upgrade-topup-dialog__plan-feature-icon" />
                                                        <span>Advanced security & compliance</span>
                                                    </li>
                                                    <li className="upgrade-topup-dialog__plan-feature">
                                                        <CheckIcon className="upgrade-topup-dialog__plan-feature-icon" />
                                                        <span>Dedicated onboarding team</span>
                                                    </li>
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="upgrade-topup-dialog__no-plans">
                                        No plans available at the moment.
                                    </div>
                                )}

                                {/* Footer buttons side by side */}
                                <div className="upgrade-topup-dialog__footer-buttons">
                                    <Button
                                        onClick={handleBuyCredits}
                                        variant="secondary"
                                        className="upgrade-topup-dialog__topup-button"
                                    >
                                        <Coins className="upgrade-topup-dialog__button-icon" />
                                        Buy Credits
                                    </Button>
                                    <Button
                                        onClick={handleUpgrade}
                                        variant="default"
                                    >
                                        Pricing Page
                                        <ArrowRight className="upgrade-topup-dialog__button-icon" />
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            /* Paid user - Show topup option */
                            <div className="upgrade-topup-dialog__topup-section">
                                <h3 className="upgrade-topup-dialog__section-title">
                                    Add More Credits
                                </h3>

                                <p className="upgrade-topup-dialog__topup-description">
                                    Purchase additional credits to continue using AI features without interruption.
                                </p>

                                <div className="upgrade-topup-dialog__topup-benefits">
                                    <div className="upgrade-topup-dialog__benefit">
                                        <Check className="upgrade-topup-dialog__benefit-icon" />
                                        <span>Instant credit activation</span>
                                    </div>
                                    <div className="upgrade-topup-dialog__benefit">
                                        <Check className="upgrade-topup-dialog__benefit-icon" />
                                        <span>No expiry on credits</span>
                                    </div>
                                    <div className="upgrade-topup-dialog__benefit">
                                        <Check className="upgrade-topup-dialog__benefit-icon" />
                                        <span>Flexible top-up amounts</span>
                                    </div>
                                </div>

                                <FeatureRestrictionWrapper
                                    feature="credit_topup"
                                    showUpgradePrompt={true}
                                >
                                    <Button
                                        onClick={handleBuyCredits}
                                        className="upgrade-topup-dialog__primary-button"
                                        size="lg"
                                    >
                                        <Coins className="upgrade-topup-dialog__button-icon" />
                                        Buy Credits
                                        <ArrowRight className="upgrade-topup-dialog__button-icon" />
                                    </Button>
                                </FeatureRestrictionWrapper>

                                {/* Cancel button for paid users */}
                                <Button
                                    onClick={onClose}
                                    variant="ghost"
                                    className="upgrade-topup-dialog__cancel-button"
                                    style={{ width: '100%' }}
                                >
                                    Maybe Later
                                </Button>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Topup Plans Dialog */}
            <TopupPlansDialog
                isOpen={isTopupDialogOpen}
                onClose={() => setIsTopupDialogOpen(false)}
            />
        </>
    );
};

export default UpgradeOrTopupDialog;

